// Endpoints RTK Query da feature sales — histórico financeiro de vendas
// (RF-09/RF-10). GET /v1/analytics/vendas com filtro de período + agrupamento
// (dia/semana/mês/trimestre/ano) + paginação por cursor. Todos os *Cents vêm em
// CENTAVOS crus → apresentar sempre via formatBRL(valueCents).
import { api } from '@/lib/api';
import type { ManagementPeriod } from '@/features/management';

/** Granularidade dos buckets devolvidos pelo back. */
export type SalesGroupBy = 'day' | 'week' | 'month' | 'quarter' | 'year';

/** Uma venda concluída via chat (linha da tabela). */
export interface SaleRow {
  id: string;
  /** Valor da venda em CENTAVOS. */
  valueCents: number;
  sellerId: string;
  sellerName: string;
  storeId: string;
  storeName: string;
  contactName: string;
  /** Data da venda em ISO-8601. */
  date: string;
  /** Chave do bucket ao qual esta venda pertence (liga em `buckets[].key`). */
  bucketKey: string;
}

/** Um agrupamento (dia/semana/mês/…) com seu subtotal. */
export interface SalesBucket {
  key: string;
  label: string;
  count: number;
  /** Subtotal do grupo em CENTAVOS. */
  subtotalCents: number;
}

export interface SalesHistoryDto {
  groupBy: SalesGroupBy;
  periodTotal: {
    count: number;
    /** Total do período em CENTAVOS. */
    valueCents: number;
  };
  buckets: SalesBucket[];
  sales: {
    items: SaleRow[];
    /** `null` quando não há mais páginas. */
    nextCursor: string | null;
  };
}

export interface SalesHistoryArgs {
  /** Preset de período (reusa o filtro global). Omitido quando há from/to custom. */
  period?: ManagementPeriod;
  /** Intervalo custom date-only (YYYY-MM-DD) — os dois juntos. */
  from?: string;
  to?: string;
  /** Escopo de loja. Ausente ⇒ back escopa pelo papel do usuário. */
  storeId?: string;
  groupBy: SalesGroupBy;
  /** Cursor da próxima página (paginação "carregar mais"). */
  cursor?: string;
  limit?: number;
}

function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

export const salesApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Paginação por cursor com acúmulo: o `cursor` é removido da chave de cache
    // (serializeQueryArgs) para que todas as páginas de um mesmo filtro caiam na
    // mesma entrada; `merge` concatena as novas linhas. Trocar período/loja/
    // agrupamento gera uma chave nova → acúmulo reinicia naturalmente.
    salesHistory: build.query<SalesHistoryDto, SalesHistoryArgs>({
      query: ({ period, from, to, storeId, groupBy, cursor, limit }) =>
        `/analytics/vendas${qs({
          period,
          from,
          to,
          storeId,
          groupBy,
          cursor,
          limit: limit?.toString(),
        })}`,
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { cursor: _cursor, ...rest } = queryArgs;
        return `${endpointName}(${JSON.stringify(rest)})`;
      },
      merge: (currentCache, newData, { arg }) => {
        // Sem cursor = primeira página / refresh → substitui (reseta o acúmulo).
        if (!arg.cursor) return newData;
        currentCache.sales.items.push(...newData.sales.items);
        currentCache.sales.nextCursor = newData.sales.nextCursor;
        currentCache.periodTotal = newData.periodTotal;
        currentCache.buckets = newData.buckets;
        currentCache.groupBy = newData.groupBy;
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: ['SalesHistory'],
    }),
  }),
  overrideExisting: false,
});

export const { useSalesHistoryQuery } = salesApi;
